import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import { INTERNAL_ROUTES } from '../../constants/urls';
import { shareVoteMate } from '@/utils/nativeShare';
import { useSurveyContext } from '@/contexts/SurveyContext';
import useAnalytics from '../../hooks/useAnalytics';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import { useSettingsDialog } from '@/contexts/SettingsDialogContext';
import { useOpenMembership } from '@/hooks/useOpenMembership';

export interface AccountMenuItem {
  key: string;
  label: string;
  /** Decorative — the item's text is its accessible name. */
  icon: ReactNode;
  onClick: () => void;
}

interface UseAccountMenuItemsOptions {
  /** Mixpanel ``source`` stamped on each item's event, naming the menu it lives in. */
  source: string;
  /** Dismisses the menu (and any drawer around it) before the action runs. */
  onDismiss: () => void;
  /** Opens the referral share dialog; the caller owns that dialog's state. */
  onRefer: () => void;
  /**
   * Override for the feedback action. Defaults to opening the global survey
   * panel; the Help page passes a handler that scrolls to its embedded form so
   * the panel doesn't defer-open elsewhere.
   */
  onFeedbackClick?: () => void;
}

/**
 * The account-level actions — membership, refer, feedback, help, settings — in the
 * order every account menu renders them. Shared by the left drawer's accordion
 * and the mobile header's avatar menu so the two stay identical.
 */
export function useAccountMenuItems({
  source,
  onDismiss,
  onRefer,
  onFeedbackClick,
}: UseAccountMenuItemsOptions): AccountMenuItem[] {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const settingsDialog = useSettingsDialog();
  const { track } = useAnalytics();
  const { openSurveyPanel } = useSurveyContext();
  const openMembership = useOpenMembership();

  return [
    {
      key: 'support',
      label: t('becomeMember'),
      icon: <FavoriteIcon sx={{ fontSize: '18px', color: primitiveColors.red4 }} />,
      onClick: () => {
        track('Support VoteMate Clicked', { source });
        onDismiss();
        openMembership('account_menu');
      },
    },
    {
      key: 'refer',
      label: t('referVoteMate'),
      icon: <ShareIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />,
      // On a touch device, hand off to the OS share sheet (like the mobile app);
      // desktop and browsers without Web Share fall back to the referral dialog.
      // navigator.share runs inside this click so it keeps the user gesture.
      onClick: async () => {
        track('Refer VoteMate Clicked', { source });
        onDismiss();
        const sharedNatively = await shareVoteMate(t('shareMessage'));
        if (!sharedNatively) onRefer();
      },
    },
    {
      key: 'feedback',
      label: t('feedback'),
      icon: <FeedbackOutlinedIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />,
      onClick: () => {
        track('Feedback Opened', { source });
        onDismiss();
        if (onFeedbackClick) {
          onFeedbackClick();
        } else {
          openSurveyPanel();
        }
      },
    },
    {
      key: 'help',
      label: t('help'),
      icon: <HelpOutlineIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />,
      onClick: () => {
        track('Help Opened', { source });
        onDismiss();
        navigate(INTERNAL_ROUTES.HELP, { state: { entry: 'menu' } });
      },
    },
    {
      key: 'settings',
      label: t('settings'),
      icon: <SettingsIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />,
      onClick: () => {
        track('Settings Opened', { source });
        onDismiss();
        if (location.pathname === INTERNAL_ROUTES.SETTINGS) return;
        if (!isMobile && settingsDialog) settingsDialog.openSettings();
        else navigate(INTERNAL_ROUTES.SETTINGS);
      },
    },
  ];
}
